import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { contactsAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Mail, Phone, Clock, Trash2, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const ContactsAdmin = () => {
  const { toast } = useToast();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const response = await contactsAPI.getAll();
      setContacts(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load contacts",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await contactsAPI.updateStatus(id, status);
      toast({ title: "Success", description: "Status updated successfully" });
      loadContacts();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      await contactsAPI.delete(id);
      toast({ title: "Success", description: "Contact deleted successfully" });
      loadContacts();
      setIsDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete contact",
        variant: "destructive"
      });
    }
  };

  const viewContact = (contact) => {
    setSelectedContact(contact);
    setIsDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'contacted':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading contacts...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Contact Messages</h1>
        <p className="text-gray-600 mt-2">View and manage customer inquiries</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-blue-600">
              {contacts.filter(c => c.status === 'new').length}
            </div>
            <div className="text-sm text-gray-600">New Messages</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-yellow-600">
              {contacts.filter(c => c.status === 'contacted').length}
            </div>
            <div className="text-sm text-gray-600">In Progress</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-green-600">
              {contacts.filter(c => c.status === 'completed').length}
            </div>
            <div className="text-sm text-gray-600">Completed</div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        {contacts.map((contact) => (
          <Card key={contact.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">
                      {contact.first_name} {contact.last_name}
                    </h3>
                    <Badge className={getStatusColor(contact.status)}>
                      {contact.status}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Mail size={16} />
                      <a href={`mailto:${contact.email}`} className="hover:text-amber-600">
                        {contact.email}
                      </a>
                    </div>
                    {contact.phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={16} />
                        <a href={`tel:${contact.phone}`} className="hover:text-amber-600">
                          {contact.phone}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Clock size={16} />
                      <span>{formatDate(contact.created_at)}</span>
                    </div>
                  </div>

                  <p className="mt-3 text-gray-700 line-clamp-2">{contact.message}</p>
                </div>

                <div className="flex gap-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => viewContact(contact)}
                  >
                    <Eye size={16} className="mr-1" />
                    View
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {contacts.length === 0 && (
        <Card className="p-12 text-center">
          <Mail className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-500">No contact messages yet</p>
        </Card>
      )}

      {/* Contact Detail Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Contact Details</DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Name</Label>
                  <p className="font-semibold">
                    {selectedContact.first_name} {selectedContact.last_name}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Status</Label>
                  <div className="mt-1">
                    <Badge className={getStatusColor(selectedContact.status)}>
                      {selectedContact.status}
                    </Badge>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm text-gray-600">Email</Label>
                <p className="font-semibold">
                  <a href={`mailto:${selectedContact.email}`} className="text-amber-600 hover:underline">
                    {selectedContact.email}
                  </a>
                </p>
              </div>

              {selectedContact.phone && (
                <div>
                  <Label className="text-sm text-gray-600">Phone</Label>
                  <p className="font-semibold">
                    <a href={`tel:${selectedContact.phone}`} className="text-amber-600 hover:underline">
                      {selectedContact.phone}
                    </a>
                  </p>
                </div>
              )}

              <div>
                <Label className="text-sm text-gray-600">Received</Label>
                <p className="font-semibold">{formatDate(selectedContact.created_at)}</p>
              </div>

              <div>
                <Label className="text-sm text-gray-600">Message</Label>
                <p className="mt-2 p-4 bg-gray-50 rounded-lg whitespace-pre-wrap">
                  {selectedContact.message}
                </p>
              </div>

              <div>
                <Label className="text-sm text-gray-600 mb-2 block">Update Status</Label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUpdateStatus(selectedContact.id, 'new')}
                    className={selectedContact.status === 'new' ? 'bg-blue-50' : ''}
                  >
                    New
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUpdateStatus(selectedContact.id, 'contacted')}
                    className={selectedContact.status === 'contacted' ? 'bg-yellow-50' : ''}
                  >
                    Contacted
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUpdateStatus(selectedContact.id, 'completed')}
                    className={selectedContact.status === 'completed' ? 'bg-green-50' : ''}
                  >
                    Completed
                  </Button>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleDelete(selectedContact.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete
                </Button>
                <Button
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Label = ({ children, className }) => (
  <label className={`block text-sm font-medium ${className}`}>{children}</label>
);

export default ContactsAdmin;